import React, { useEffect, useState } from "react";
import { listContacts, createContact, updateContact, deleteContact } from "../lib/api";
import { userIdentity } from "../lib/identity";
import { toast } from "sonner";
import { Trash2, Plus, User, Phone, Mail, Pencil, X, Check } from "lucide-react";
import { useT } from "../lib/lang";

const EMPTY_FORM = { name: "", phone: "", email: "", relationship: "" };

// Light client-side validation. Backend validates again with Pydantic,
// but giving the user instant feedback avoids a round-trip per typo.
const isValidEmail = (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const isValidPhone = (v) => !v || /^\+?[0-9\s().-]{6,20}$/.test(v);

// Iter74 — `EmergencyContactsSection` is the reusable slice of the
// contacts UI (form + list). It intentionally renders NO page-level
// chrome (no page header, no bottom padding, no full-height flex
// wrapper). The full-page `ContactsPage` default export wraps this
// section with the historic page chrome — that path stays live for
// backwards-compat with any older bookmarks / deep links. The new
// citizen profile page (`/profil`) embeds this same section inside its
// own layout without any duplication of state or fetching logic.
export function EmergencyContactsSection() {
  const [t] = useT();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    // Defer the initial fetch out of the effect body so we don't call
    // setState synchronously inside the effect (cascading-renders rule).
    let cancelled = false;
    (async () => {
      try {
        // Sep 2026 bugfix — wait for any in-flight device-token
        // self-heal before fetching, otherwise a grandfathered user
        // (phone saved, no token yet) can lose the race and 401.
        await userIdentity.ready();
        const data = await listContacts();
        if (!cancelled) setContacts(data);
      } catch (_) {
        if (!cancelled) toast.error(t.errLoadContacts);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || "",
      phone: c.phone || "",
      email: c.email || "",
      relationship: c.relationship || "",
    });
    document.querySelector('[data-testid="contact-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error(t.errNameReq);
    if (!form.phone && !form.email) return toast.error(t.errPhoneOrEmail);
    if (!isValidPhone(form.phone)) return toast.error(t.errInvalidPhone);
    if (!isValidEmail(form.email)) return toast.error(t.errInvalidEmail);

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        relationship: form.relationship.trim() || null,
      };
      if (editingId) {
        const updated = await updateContact(editingId, payload);
        setContacts((prev) => prev.map((c) => (c.id === editingId ? updated : c)));
        toast.success(t.contactUpdated);
        cancelEdit();
      } else {
        const c = await createContact(payload);
        setContacts((prev) => [c, ...prev]);
        setForm(EMPTY_FORM);
        toast.success(t.contactAdded);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (editingId ? t.errUpdateContact : t.errSaveContact));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteContact(id);
      setContacts((prev) => prev.filter((c) => c.id !== id));
      if (editingId === id) cancelEdit();
      toast.success(t.contactRemoved);
    } catch (_) {
      toast.error(t.errDeleteContact);
    }
  };

  return (
    <div className="flex flex-col" data-testid="contacts-section">
      <form
        onSubmit={submit}
        className={`mx-5 mt-2 mb-6 border-2 bg-white p-4 space-y-3 transition-colors ${editingId ? "border-mali-yellow" : "border-rescue-line"}`}
        data-testid="contact-form"
      >
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-rescue-mute">
            {editingId ? t.editContact : t.addContact}
          </div>
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              data-testid="contact-edit-cancel-btn"
              className="text-[10px] font-bold uppercase tracking-[0.15em] text-rescue-mute hover:text-rescue-red flex items-center gap-1"
            >
              <X className="w-3 h-3" /> {t.cancel}
            </button>
          )}
        </div>
        <div className="space-y-2">
          <input
            data-testid="contact-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={t.fullName}
            className="w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-2.5 text-sm"
          />
          <input
            data-testid="contact-phone-input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder={t.phonePh}
            inputMode="tel"
            className="w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-2.5 text-sm"
          />
          <input
            data-testid="contact-email-input"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder={t.emailPh}
            inputMode="email"
            className="w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-2.5 text-sm"
          />
          <input
            data-testid="contact-rel-input"
            value={form.relationship}
            onChange={(e) => setForm({ ...form, relationship: e.target.value })}
            placeholder={t.relPh}
            className="w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-2.5 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="contact-submit-btn"
          className={`w-full text-white py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors flex items-center justify-center gap-2 disabled:opacity-60 ${
            editingId ? "bg-mali-navy hover:bg-mali-navy/90" : "bg-rescue-ink hover:bg-rescue-red"
          }`}
        >
          {editingId ? (
            <><Check className="w-4 h-4" /> {saving ? t.saving : t.saveChanges}</>
          ) : (
            <><Plus className="w-4 h-4" /> {saving ? t.saving : t.add}</>
          )}
        </button>
      </form>

      <div className="px-5 pb-8">
        <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-rescue-mute mb-2">
          {loading ? t.loading : t.contactCount(contacts.length)}
        </div>
        <div className="border-2 border-rescue-line bg-white" data-testid="contacts-list">
          {contacts.length === 0 && !loading && (
            <div className="px-4 py-8 text-center text-sm text-rescue-mute">{t.noContacts}</div>
          )}
          {contacts.map((c) => {
            const isEditing = editingId === c.id;
            return (
              <div
                key={c.id}
                className={`px-4 py-3 border-b border-rescue-line last:border-b-0 flex items-start justify-between gap-3 ${isEditing ? "bg-mali-yellow/10" : ""}`}
                data-testid={`contact-row-${c.id}`}
              >
                <div className="min-w-0">
                  <div className="font-display text-base font-black tracking-tight flex items-center gap-2">
                    <User className="w-4 h-4 text-rescue-red" />{c.name}
                    {c.relationship && <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-rescue-mute">· {c.relationship}</span>}
                  </div>
                  {c.phone && (<div className="font-mono text-[12px] text-rescue-ink flex items-center gap-1.5 mt-1"><Phone className="w-3.5 h-3.5" />{c.phone}</div>)}
                  {c.email && (<div className="font-mono text-[12px] text-rescue-ink flex items-center gap-1.5 mt-0.5"><Mail className="w-3.5 h-3.5" />{c.email}</div>)}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button
                    onClick={() => startEdit(c)}
                    data-testid={`contact-edit-${c.id}`}
                    className={`p-2 border-2 transition-colors ${isEditing ? "border-mali-yellow text-mali-yellow" : "border-rescue-line hover:border-mali-navy hover:text-mali-navy"}`}
                    aria-label="Modifier le contact"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(c.id)}
                    data-testid={`contact-delete-${c.id}`}
                    className="p-2 border-2 border-rescue-line hover:border-rescue-red hover:text-rescue-red transition-colors"
                    aria-label="Supprimer le contact"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Backwards-compat default export — the standalone `/contacts` page.
// The new `/profil` page renders <EmergencyContactsSection /> directly.
export default function ContactsPage() {
  const [t] = useT();
  return (
    <div className="flex-1 flex flex-col" data-testid="contacts-page">
      <header className="px-5 pt-5 pb-3">
        <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-rescue-mute">{t.contactsKicker}</div>
        <h1 className="font-display text-3xl font-black tracking-tight leading-none mt-1">{t.contactsTitle}</h1>
        <p className="text-sm text-rescue-mute mt-2">{t.contactsIntro}</p>
      </header>
      <EmergencyContactsSection />
    </div>
  );
}
